import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ProvidedJobPostsResponse } from '../types/ProvidedJobPost';
import type { AllianceFeeFormValues } from '../schemas/allianceFeeSchema';
import { allianceFeeSchema } from '../schemas/allianceFeeSchema';
import { Button } from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import Input from '@/components/Input';
import Select from '@/components/Select';
import { Textarea } from '@/components/Textarea';

// 条件の種類セレクトの選択肢
const FEE_MODE_OPTIONS: { value: string; label: string }[] = [
  { value: 'none', label: '設定なし' },
  { value: 'rate', label: '成約手数料率' },
  { value: 'amount', label: '成約金額' },
];

const NOTE_MAX_LENGTH = 500;

type Props = {
  // 編集対象の求人情報（初期値の組み立てに使用）
  jobPost: ProvidedJobPostsResponse;
  // フォーム送信時のコールバック（バリデーション通過後に呼ばれる）
  onSubmit: (values: AllianceFeeFormValues) => void;
  // 保存処理中フラグ（ボタン無効化・テキスト切り替えに使用）
  isSaving: boolean;
};

/**
 * アライアンス成約条件 + 提供除外フラグの編集フォーム。
 * React Hook Form + zodResolver でバリデーションを行い、値を onSubmit に渡す。
 */
export default function AllianceFeeForm({ jobPost, onSubmit, isSaving }: Props) {
  // 既存の成約条件から初期モードを決定する
  const initialFee = jobPost.alliance_fee ?? null;
  const initialMode: AllianceFeeFormValues['mode'] =
    initialFee?.fee_type === 'rate'
      ? 'rate'
      : initialFee?.fee_type === 'amount'
        ? 'amount'
        : 'none';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<AllianceFeeFormValues>({
    resolver: zodResolver(allianceFeeSchema),
    defaultValues: {
      mode: initialMode,
      fee_rate: initialFee?.fee_rate?.toString() ?? '',
      fee_amount: initialFee?.fee_amount?.toString() ?? '',
      note: initialFee?.note ?? '',
      disabled: initialFee?.disabled ?? false,
    },
  });

  const mode = watch('mode');
  const noteValue = watch('note') ?? '';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-lg">
      {/* アライアンス先への提供除外フラグ。成約条件入力の手前に配置 */}
      <div className="rounded border border-solid border-gray-200 bg-gray-50 p-3">
        <Controller
          control={control}
          name="disabled"
          render={({ field }) => (
            <Checkbox checked={field.value} onChange={field.onChange} disabled={isSaving}>
              <span className="text-body-sm">この求人をアライアンス先に提供しない</span>
            </Checkbox>
          )}
        />
        <p className="mt-1 pl-7 text-xs text-gray-500">
          ONにすると、新規にこの求人がアライアンス先エージェントの一覧・詳細に表示されなくなり、新規応募も受け付けません。既に応募中のアライアンス選考は引き続き利用できます。
        </p>
      </div>

      {/* 条件の種類セレクト */}
      <div>
        <label htmlFor="alliance-fee-mode" className="block text-heading-xxs">
          条件の種類
        </label>
        <div className="mt-2 w-[240px]">
          <Select
            id="alliance-fee-mode"
            items={FEE_MODE_OPTIONS}
            value={mode}
            onValueChange={(value) =>
              setValue('mode', value as AllianceFeeFormValues['mode'], { shouldValidate: true })
            }
            disabled={isSaving}
          />
        </div>
      </div>

      {/* 成約手数料率（rate モード時のみ表示） */}
      {mode === 'rate' && (
        <div>
          <label htmlFor="alliance-fee-rate" className="block text-heading-xxs">
            成約手数料率
          </label>
          <div className="mt-2 flex items-center gap-2">
            <Input
              id="alliance-fee-rate"
              type="number"
              step="0.1"
              min="0.1"
              max="100.0"
              className="w-[160px]"
              disabled={isSaving}
              {...register('fee_rate')}
            />
            <span className="text-body-sm">%</span>
          </div>
          {errors.fee_rate && (
            <p className="mt-1 text-xs text-red-600" role="alert">
              {errors.fee_rate.message}
            </p>
          )}
        </div>
      )}

      {/* 成約手数料額（amount モード時のみ表示） */}
      {mode === 'amount' && (
        <div>
          <label htmlFor="alliance-fee-amount" className="block text-heading-xxs">
            成約手数料額
          </label>
          <div className="mt-2 flex items-center gap-2">
            <Input
              id="alliance-fee-amount"
              type="number"
              step="1"
              min="1"
              max="99999"
              className="w-[160px]"
              disabled={isSaving}
              {...register('fee_amount')}
            />
            <span className="text-body-sm">万円</span>
          </div>
          {errors.fee_amount && (
            <p className="mt-1 text-xs text-red-600" role="alert">
              {errors.fee_amount.message}
            </p>
          )}
        </div>
      )}

      {/* 備考（文字数カウンタ付き） */}
      <div>
        <label htmlFor="alliance-fee-note" className="block text-heading-xxs">
          備考
        </label>
        <p className="mt-1 text-xs text-gray-500">
          提携エージェントに伝える補足事項があれば入力してください（{NOTE_MAX_LENGTH}文字以内）。
        </p>
        <div className="mt-2">
          <Textarea
            id="alliance-fee-note"
            className="min-h-[120px]"
            maxLength={NOTE_MAX_LENGTH}
            disabled={isSaving}
            {...register('note')}
          />
        </div>
        {/* 文字数カウンタ */}
        <p
          className={`mt-1 text-right text-xs ${noteValue.length > NOTE_MAX_LENGTH ? 'text-red-600' : 'text-gray-400'}`}
        >
          {noteValue.length} / {NOTE_MAX_LENGTH}
        </p>
        {errors.note && (
          <p className="mt-1 text-xs text-red-600" role="alert">
            {errors.note.message}
          </p>
        )}
      </div>

      {/* 保存ボタン */}
      <div className="flex justify-end">
        <Button type="submit" color="primary" size="md" disabled={isSaving}>
          {isSaving ? '保存中...' : '保存'}
        </Button>
      </div>
    </form>
  );
}
